import { Component, inject, OnInit } from '@angular/core';
import { Menubar } from 'primeng/menubar';
import { NgClass } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Button } from 'primeng/button';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { PanelMenu } from 'primeng/panelmenu';
import { Store } from '@ngrx/store';
import { AuthState } from '../auth/store/auth.state';
import * as authAction from '../auth/store/auth.actions';
import { ThemeService } from '../shared/services/theme.service';
import { Tooltip } from 'primeng/tooltip';
import { CookieService } from '../shared/services/cookie.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { selectUser } from '../auth/store/auth.selector';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserEditDialogComponent } from '../management/components/user-edit-dialog/user-edit-dialog.component';
import { User } from '../shared/models/user.model';
import * as UserActions from '../management/store/user.actions';
import { signal, OnDestroy } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { isAdminUser } from '../shared/utilities';

@Component({
  selector: 'layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    Menubar,
    NgClass,
    RouterOutlet,
    Button,
    PrimeTemplate,
    Menu,
    PanelMenu,
    Tooltip,
    UserEditDialogComponent
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {

  private store$ = inject(Store<{ auth: AuthState }>)
  private themeService = inject(ThemeService)
  private cookieService = inject(CookieService)
  private jwtHelper = inject(JwtHelperService)
  private http = inject(HttpClient)
  private router = inject(Router)

  sidebarVisible: boolean = true;
  sidebarMenuItems: MenuItem[] = []
  userMenuItems: MenuItem[] = [];
  username: string = '';
  logoPath = '/home_logo.png';

  currentUser = toSignal(this.store$.select(selectUser));
  profileDialogVisible = signal(false);
  savingProfile = signal(false);

  private actions$ = inject(Actions);
  private destroy$ = new Subject<void>();

  // Expose theme service signal for template
  isDarkMode = this.themeService.isDarkMode;

  ngOnInit() {

    const token = this.cookieService.getCookie('jwt');
    let userIsAdmin = false;
    if (token) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(token);
        userIsAdmin = isAdminUser(decodedToken?.role);
        this.username = 'Hi, ' + (decodedToken?.name || decodedToken?.login || 'User');

        // Populate the store with user information from token
        const user: User = {
          id: decodedToken.id,
          username: decodedToken.login || decodedToken.username || '',
          login: decodedToken.login,
          name: decodedToken.name,
          email: decodedToken.email,
          role: decodedToken.role,
          phone_number: decodedToken.phone_number || '',
          is_active: decodedToken.is_active,
          is_staff: decodedToken.is_staff,
          is_superuser: decodedToken.is_superuser
        };
        this.store$.dispatch(authAction.setUser({ user }));
        // Refresh user profile from server to ensure latest data
        this.store$.dispatch(authAction.loadUserProfile());
      } catch (error) {
        this.username = 'Not Found';
      }
    } else {
      this.username = 'Not Found';
    }

    this.sidebarMenuItems = [
      {
        label: 'Home',
        items: [
          {
            label: 'Dashboard',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/dashboard']
          },
          {
            label: 'Map',
            icon: 'fa-solid fa-fw fa-map-location-dot',
            url: '/map'
          },
          {
            label: 'Maintenance Records',
            icon: 'pi pi-fw pi-wrench',
            routerLink: ['/maintenance-records'],
            command: () => {
              this.router.navigate(['/maintenance-records']);
            }
          }
        ]
      },
      {
        label: 'Assets',
        items: [
          {
            label: 'Bollard',
            icon: 'fa-solid fa-fw fa-circle-exclamation',
            routerLink: ['/bollards']
          },
          {
            label: 'Bridge',
            icon: 'fa-solid fa-fw fa-bridge',
            routerLink: ['/bridges']
          },
          {
            label: 'Bus Stop',
            icon: 'fa-solid fa-fw fa-bus',
            routerLink: ['/bus-stops']
          },
          {
            label: 'Fence',
            icon: 'fa-solid fa-fw fa-bars',
            routerLink: ['/fences']
          },
          {
            label: 'GeoTech Structure',
            icon: 'fa-solid fa-fw fa-mountain',
            routerLink: ['/geo-tech-structures']
          },
          {
            label: 'Guard Rail',
            icon: 'fa-solid fa-fw fa-road-barrier',
            routerLink: ['/guardrails']
          },
          {
            label: 'Multipurpose Hall',
            icon: 'fa-solid fa-fw fa-building-columns',
            routerLink: ['/multipurpose-halls']
          },
          {
            label: 'Parking',
            icon: 'fa-solid fa-fw fa-square-parking',
            routerLink: ['/parkings']
          },
          {
            label: 'Pavement',
            icon: 'fa-solid fa-fw fa-road',
            routerLink: ['/pavements']
          },
          {
            label: 'Public Toilet',
            icon: 'fa-solid fa-fw fa-restroom',
            routerLink: ['/public-toilets']
          },
          {
            label: 'Road Kerb',
            icon: 'fa-solid fa-fw fa-border-top-left',
            routerLink: ['/road-kerbs']
          },
          {
            label: 'Road Marking',
            icon: 'fa-solid fa-fw fa-grip-lines',
            routerLink: ['/road-markings']
          },
          {
            label: 'Traffic Light',
            icon: 'fa-solid fa-fw fa-traffic-light',
            routerLink: ['/traffic-lights']
          },
          {
            label: 'Traffic Sign',
            icon: 'fa-solid fa-fw fa-diamond-turn-right',
            routerLink: ['/traffic-signs']
          },
          {
            label: 'Walkway',
            icon: 'fa-solid fa-fw fa-person-walking',
            routerLink: ['/walkways']
          }
        ]
      },
      {
        label: 'Archived',
        items: [
          {
            label: 'Bin Centre',
            icon: 'fa-solid fa-fw fa-dumpster',
            routerLink: ['/bin-centres']
          },
          {
            label: 'Drainage',
            icon: 'fa-solid fa-fw fa-water',
            routerLink: ['/drainages']
          },
          {
            label: 'Drainage Cover',
            icon: 'fa-solid fa-fw fa-circle-dot',
            routerLink: ['/drainage-covers']
          }
        ]
      },
      {
        label: 'Metadata',
        items: [
          {
            label: 'Roads',
            icon: 'fa-solid fa-fw fa-road',
            routerLink: ['/roads']
          },
          {
            label: 'Bounds',
            icon: 'fa-solid fa-fw fa-border-all',
            routerLink: ['/bounds']
          },
          {
            label: 'Zones',
            icon: 'fa-solid fa-fw fa-draw-polygon',
            routerLink: ['/zones']
          },
          {
            label: 'Asset Codes',
            icon: 'fa-solid fa-fw fa-tags',
            routerLink: ['/asset-codes']
          },
          {
            label: 'LCCA',
            icon: 'fa-solid fa-fw fa-chart-line',
            routerLink: ['/lcca']
          },
          {
            label: 'LCCA Unit',
            icon: 'fa-solid fa-fw fa-calculator',
            routerLink: ['/lcca-unit']
          },
          {
            label: 'LCCA Trigger',
            icon: 'fa-solid fa-fw fa-bolt',
            routerLink: ['/lcca-trigger']
          },
          {
            label: 'LCCA Periodicals',
            icon: 'fa-solid fa-fw fa-calendar-days',
            routerLink: ['/lcca-periodicals']
          },
          {
            label: 'LCCA Type',
            icon: 'fa-solid fa-fw fa-list',
            routerLink: ['/lcca-type']
          },
          {
            label: 'Asset Codes Group Distinct',
            icon: 'fa-solid fa-fw fa-layer-group',
            routerLink: ['/asset-codes-group-distinct']
          },
        ]
      },
      {
        label: 'Management',
        items: [
          // {
          //   label: 'Settings',
          //   icon: 'fa-solid fa-fw fa-gear',
          //   routerLink: ['/settings']
          // },
          ...(userIsAdmin ? [{
            label: 'Users',
            icon: 'fa-solid fa-fw fa-users',
            routerLink: ['/users']
          }] : []),
          {
            label: 'Audit Trails',
            icon: 'fa-solid fa-fw fa-clock-rotate-left',
            routerLink: ['/audit-trails']
          },
          {
            label: 'Login Attempts',
            icon: 'fa-solid fa-fw fa-right-to-bracket',
            routerLink: ['/audit-trails/login-attempts']
          },
          {
            label: 'User Guide',
            icon: 'fa-solid fa-fw fa-book',
            command: () => {
              this.openUserManualGdrive();
            }
          },
          {
            label: 'AMP',
            icon: 'fa-solid fa-fw fa-file-pdf',
            command: () => {
              this.openUserManualGdrive();
            }
          },
        ]
      }
    ];

    this.userMenuItems = [
      {
        label: 'Edit Profile',
        icon: 'pi pi-fw pi-user-edit',
        command: () => {
          this.profileDialogVisible.set(true);
        }
      },
      {
        separator: true
      },
      {
        label: 'Logout',
        icon: 'pi pi-fw pi-sign-out',
        command: () => {
          this.store$.dispatch(authAction.logout())
        }
      }
    ]

    // Expand the correct panel and highlight the active menu item
    this.setActiveMenuItem(this.router.url);
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      this.setActiveMenuItem((event as NavigationEnd).urlAfterRedirects);
    });

    // Listen for profile update success
    this.actions$.pipe(
      ofType(UserActions.updateUserSuccess),
      takeUntil(this.destroy$)
    ).subscribe(({ user }) => {
      if (this.profileDialogVisible()) {
        this.closeProfileDialog();
        // Update the auth user state so the topbar reflects changes without refresh
        this.store$.dispatch(authAction.updateUserStore({ user }));
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  private setActiveMenuItem(url: string) {
    for (const group of this.sidebarMenuItems) {
      let hasActive = false;
      if (group.items) {
        for (const item of group.items) {
          const link = item.routerLink?.[0];
          const isActive = !!link && url.startsWith(link);
          item.styleClass = isActive ? 'active-menuitem' : undefined;
          if (isActive) {
            hasActive = true;
          }
        }
      }
      group.expanded = hasActive;
    }
  }

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;
  }

  toggleDarkMode() {
    this.themeService.toggleDarkMode();
  }

  onProfileSave(userData: Partial<User>) {
    const user = this.currentUser();
    if (user && user.id) {
      this.savingProfile.set(true);
      this.store$.dispatch(UserActions.updateUser({ id: user.id, user: userData }));
      // We can also update the local auth state if needed,
      // but usually the effect handles global notifications.
      // After success, we should close the dialog.
      // For now, let's assume it works or add an effect listener.
    }
  }

  closeProfileDialog() {
    this.profileDialogVisible.set(false);
    this.savingProfile.set(false);
  }

  downloadUserGuide() {
    const url = `${environment.apiUrl}/common/documents/user-guide`;

    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        // link.download = 'User Guide.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      },
      error: (error) => {
        console.error('Error downloading user guide:', error);
      }
    });
  }

  openUserManualGdrive() {
    //const url = `${environment.apiUrl}/common/documents/gdrive`;
    const url = 'https://docs.google.com/document/d/1QurD1RDov7ea8oUU8JxQYq5WIL6QxAedEBtL34s9IRA/edit?tab=t.0'
    window.open(url, '_blank');
  }

  openAMPGdrive() {
    const url = 'https://drive.google.com/file/d/1jGc8RLq8nrfYfOl9hWkZcsU-4VXAoylQ/view'
    window.open(url, '_blank');
  }
}

